import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography } from '@mui/material';
import { apiClient } from '../../lib/apiClient';

type ManageUserRow = {
  name?: string;
  mail_address?: string;
  department?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const ManageUsersDialog: React.FC<Props> = ({ open, onClose }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<ManageUserRow[]>([]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get('/get_manage_users');
        const list = Array.isArray(res?.data) ? res?.data : [];
        if (!cancelled) setRows(list);
      } catch {
        if (!cancelled) {
          setRows([]);
          setError('取得に失敗しました。');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open]);

  const grouped = React.useMemo(() => {
    const map = new Map<string, { department: string; users: string[] }>();
    (rows ?? []).forEach((row) => {
      const dept = String(row?.department ?? '').trim() || '未設定';
      const name = String(row?.name ?? '').trim();
      const mail = String(row?.mail_address ?? '').trim();
      const label = mail ? `${name || '—'}<${mail}>` : (name || '—');
      if (!map.has(dept)) map.set(dept, { department: dept, users: [] });
      map.get(dept)?.users.push(label);
    });
    return Array.from(map.values());
  }, [rows]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      BackdropProps={{ invisible: true }}
      sx={{ zIndex: 2101 }}
    >
      <DialogTitle>管理メンバー名簿</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Typography variant="body2">読込中...</Typography>
        )}
        {!loading && error && (
          <Typography variant="body2" color="error">{error}</Typography>
        )}
        {!loading && !error && (
          <TableContainer component={Paper}>
            <Table size="small" aria-label="manage users table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '20%' }}>課</TableCell>
                  <TableCell>管理者</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grouped.length > 0 ? (
                  grouped.map((group) => (
                    <TableRow key={group.department}>
                      <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>{group.department}</TableCell>
                      <TableCell>
                        {group.users.map((u, idx) => (
                          <div key={`${group.department}-${idx}`}>{u}</div>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ color: 'text.secondary' }}>データはありません。</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageUsersDialog;
